(() => {
  "use strict";

  const namespace = "GuruShopCartDrawer";
  if (window[namespace]?.version === 1) return;

  const drawerRootSelector = "[data-guru-cart-drawer]";
  const pageRootSelector = "[data-guru-cart-page]";
  const rootSelector = `${drawerRootSelector}, ${pageRootSelector}`;
  let inputModality = "pointer";
  const retryMutations = new Map();
  const focusRequests = new Map();
  const announcementRequests = new Map();
  const quantityLimitRequests = new Map();
  const statusTimers = new WeakMap();
  const pendingButtonTimers = new WeakMap();
  const reducedMotionRuns = new WeakMap();
  const initializedRoots = new WeakSet();
  const translationCache = new WeakMap();
  let giftRecoveryPromise = null;
  let sealedGiftSnapshot = "";
  const approvedGiftCheckouts = new WeakSet();
  const receiptPattern = /^1\.(DKK|SEK)\.[a-f0-9]{64}\.[a-f0-9]{64}$/;

  const cartRoot = (node) => node?.closest?.(rootSelector) || null;
  const commerceLeaseBusy = () => Boolean(
    window.GuruShopCommerceLease?.isLocked?.(),
  );
  const sectionIdFor = (root) => String(root?.dataset.guruCartSectionId || "").trim();
  const requestKeyFor = (root) => sectionIdFor(root) || String(root?.id || "");
  const isDrawerRoot = (root) => root?.matches?.(drawerRootSelector);
  const isPageRoot = (root) => root?.matches?.(pageRootSelector);
  const route = (path) => {
    const root = window.Shopify?.routes?.root || "/";
    return `${root.endsWith("/") ? root : `${root}/`}${String(path).replace(/^\/+/, "")}`;
  };

  const fallbackCopy = Object.freeze({
    adding: "Adding …",
    checkoutBusy: "Payment is being prepared. Please wait before changing the cart.",
    cartBusy: "The cart is updating. Please wait a moment.",
    checkoutReady: "Continue to secure payment",
    checkoutLoading: "Preparing payment …",
    resolveChanges: "Resolve changes to continue",
    unchanged: "Your cart has not changed. Please try again.",
    updateFailed: "The cart could not be updated. __ERROR__",
    tryAgain: "Please try again in a moment.",
    invalidCart: "Shopify returned an invalid cart.",
    viewFetchFailed: "The cart view could not be loaded.",
    invalidView: "Shopify returned an invalid cart view.",
    incompleteView: "Shopify returned an incomplete cart view.",
    viewNotUpdated: "The cart view was not updated. Please try again.",
    cartUpdated: "The cart has been updated.",
    giftNormalizeFailed: "The free gifts in the cart could not be normalized.",
    giftRemovedOne: "Your free gift at __THRESHOLD__ was removed because the subtotal is below the gift threshold.",
    giftRemovedBoth: "Your free gifts were removed because the subtotal is below the gift thresholds.",
    giftNotConfirmed: "Shopify did not confirm the selected gift.",
    sellingPlanRequired: "This product requires a valid delivery interval and cannot be added as a one-time purchase.",
    statusNotConfirmed: "The cart status could not be confirmed.",
    itemNotConfirmed: "Shopify did not confirm the selected cart item.",
    productAdded: "The product was added as a one-time purchase.",
    giftFallback: "gift",
    giftAdding: "Adding gift …",
    giftAlreadySelected: "__TITLE__ is already selected for this gift tier.",
    giftThresholdLost: "The cart no longer qualifies for this gift tier.",
    giftSelected: "__TITLE__ was selected as a free gift.",
    currentOneTime: "Current purchase option: One-time purchase.",
    fixedNotActivated: "Recurring delivery was not activated.",
    changedToOneTime: "The product was changed to a one-time purchase.",
    fixedChooseInterval: "Recurring delivery is selected. Choose an interval to complete the change. Until then, the line remains a one-time purchase.",
    chooseInterval: "Choose a delivery interval to activate recurring delivery.",
    chooseValidInterval: "Choose a valid delivery interval.",
    fixedUpdated: "Recurring delivery was updated to __INTERVAL__.",
    giftRemoved: "The gift was removed from the cart.",
    productRemoved: "The product was removed from the cart.",
    quantityUpdated: "The quantity was updated to __COUNT__.",
    inventoryLimit: "Unfortunately, there are no more __TITLE__ in stock.",
    inventoryCapped: "Only __COUNT__ __TITLE__ are in stock. The cart was updated to __COUNT__.",
    checkoutInterval: "Choose a delivery interval before continuing to payment.",
    checkoutUnavailable: "Remove unavailable items before continuing to payment.",
    checkoutRefresh: "The cart must be updated before continuing to payment.",
    giftRecovered: "The free gift in the cart was updated.",
    viewMissing: "The cart view is unavailable.",
  });

  const translationsFor = (root) => {
    if (!root) return fallbackCopy;
    const cached = translationCache.get(root);
    if (cached) return cached;
    let translations = {};
    try {
      translations = JSON.parse(
        root.querySelector("[data-guru-cart-translations]")?.textContent || "{}",
      );
    } catch {
      translations = {};
    }
    const merged = Object.freeze({ ...fallbackCopy, ...translations });
    translationCache.set(root, merged);
    return merged;
  };

  const copy = (root, key, replacements = {}) => {
    let value = String(translationsFor(root)[key] || fallbackCopy[key] || key);
    for (const [token, replacement] of Object.entries(replacements)) {
      value = value.replaceAll(`__${token}__`, String(replacement));
    }
    return value;
  };

  const announce = (root, message) => {
    const status = root?.querySelector("[data-guru-cart-status]");
    if (!status) return;
    window.clearTimeout(statusTimers.get(root));
    status.textContent = "";
    window.requestAnimationFrame(() => {
      if (!root.isConnected) return;
      status.textContent = message;
      statusTimers.set(root, window.setTimeout(() => {
        status.textContent = "";
        statusTimers.delete(root);
      }, 7000));
    });
  };

  const pendingPlanControls = (root) =>
    [...root.querySelectorAll('[data-guru-cart-plan-toggle][data-pending="true"]')];

  const quantityRules = (input) => {
    const minimum = Math.max(1, Number(input?.min) || 1);
    const step = Math.max(1, Number(input?.step) || 1);
    const maximumAttribute = input?.getAttribute?.("max");
    const hasMaximum = maximumAttribute !== null && String(maximumAttribute).trim() !== "";
    const requestedMaximum = Number(maximumAttribute);
    const hardMaximum = hasMaximum && Number.isFinite(requestedMaximum)
      ? Math.max(minimum, requestedMaximum)
      : minimum;
    const maximum = hasMaximum
      ? minimum + (Math.floor((hardMaximum - minimum) / step) * step)
      : null;
    return { minimum, maximum, step };
  };

  const quantityOnLattice = (input, value, direction = "nearest") => {
    const { minimum, maximum, step } = quantityRules(input);
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return minimum;
    const position = (numeric - minimum) / step;
    const rounded = direction === "down"
      ? Math.floor(position)
      : direction === "up"
        ? Math.ceil(position)
        : Math.round(position);
    const latticeValue = minimum + (rounded * step);
    const upperBound = maximum ?? Number.MAX_SAFE_INTEGER;
    return Math.max(minimum, Math.min(upperBound, latticeValue));
  };

  const setShadowFooterVisibility = (root) => {
    const footer = root?.shadowRoot?.querySelector('[part~="footer"]');
    if (!footer) return;
    const hasFooter = Boolean(root.querySelector('[slot="footer"]'));
    const hidden = !hasFooter
      || root.getAttribute("aria-busy") === "true"
      || root.dataset.guruCartError === "true";
    footer.hidden = hidden;
    footer.toggleAttribute("aria-hidden", hidden);
  };

  const mutationControls = (root) => [...root.querySelectorAll([
    "[data-guru-cart-quantity-action]",
    "[data-guru-cart-quantity]",
    "[data-guru-cart-plan-toggle]",
    "[data-guru-cart-frequency]",
    "[data-guru-cart-quick-add]",
    "[data-guru-cart-gift-select]",
  ].join(","))];

  const setControlsLocked = (controls, locked) => {
    for (const control of controls) {
      const supportsDisabled = "disabled" in control;
      if (locked) {
        if (!Object.hasOwn(control.dataset, "guruCartWasDisabled")) {
          control.dataset.guruCartWasDisabled = supportsDisabled && control.disabled ? "true" : "false";
          control.dataset.guruCartWasTabindex = control.getAttribute("tabindex") ?? "";
        }
        if (supportsDisabled) control.disabled = true;
        else {
          control.setAttribute("aria-disabled", "true");
          control.setAttribute("tabindex", "-1");
        }
      } else if (Object.hasOwn(control.dataset, "guruCartWasDisabled")) {
        if (supportsDisabled) control.disabled = control.dataset.guruCartWasDisabled === "true";
        else {
          control.removeAttribute("aria-disabled");
          const previousTabindex = control.dataset.guruCartWasTabindex;
          if (previousTabindex) control.setAttribute("tabindex", previousTabindex);
          else control.removeAttribute("tabindex");
        }
        delete control.dataset.guruCartWasDisabled;
        delete control.dataset.guruCartWasTabindex;
      }
    }
  };

  const setMutationControlsLocked = (root, locked) => {
    setControlsLocked(mutationControls(root), locked);
  };

  const pendingSpinner = (copy) => [
    '<svg class="cart-operation-spinner" viewBox="0 0 20 20" aria-hidden="true">',
    '<circle cx="10" cy="10" r="7"></circle><path d="M10 3a7 7 0 0 1 7 7"></path></svg>',
    `<span>${copy}</span>`,
  ].join("");

  const setPendingButton = (button, busy, buttonCopy) => {
    if (!button) return;
    const previousTimer = pendingButtonTimers.get(button);
    if (previousTimer) window.clearTimeout(previousTimer);
    pendingButtonTimers.delete(button);

    if (busy) {
      if (!Object.hasOwn(button.dataset, "guruCartPendingMarkup")) {
        button.dataset.guruCartPendingMarkup = button.innerHTML;
        button.dataset.guruCartPendingDisabled = button.disabled ? "true" : "false";
      }
      button.disabled = true;
      button.setAttribute("aria-busy", "true");
      pendingButtonTimers.set(button, window.setTimeout(() => {
        if (!button.isConnected || button.getAttribute("aria-busy") !== "true") return;
        button.innerHTML = pendingSpinner(buttonCopy);
        pendingButtonTimers.delete(button);
      }, 150));
      return;
    }

    if (Object.hasOwn(button.dataset, "guruCartPendingMarkup")) {
      button.innerHTML = button.dataset.guruCartPendingMarkup;
      button.disabled = button.dataset.guruCartPendingDisabled === "true";
      delete button.dataset.guruCartPendingMarkup;
      delete button.dataset.guruCartPendingDisabled;
    }
    button.removeAttribute("aria-busy");
  };

  const setLocalOperation = (root, busy, { line = null, button = null, copy: operationCopy = "" } = {}) => {
    if (!root) return;
    root.dataset.guruCartLocalMutationBusy = String(busy);
    if (line) {
      line.toggleAttribute("aria-busy", busy);
      line.classList.toggle("cart-line--pending", busy);
      setControlsLocked(mutationControls(line), busy);
    }
    setPendingButton(button, busy, operationCopy || copy(root, "adding"));
    syncCheckoutState(root);
  };

  const memberCheckoutBusy = (root) => root?.dataset.minGuruCheckoutBusy === "true";
  const cartMutationBusy = (root) => root?.dataset.minGuruCartMutationBusy === "true";
  const localMutationBusy = (root) => root?.dataset.guruCartLocalMutationBusy === "true";

  const blockMutationDuringCheckout = (root) => {
    const checkoutBusy = memberCheckoutBusy(root);
    const mutationBusy = commerceLeaseBusy()
      || cartMutationBusy(root)
      || localMutationBusy(root)
      || root?.getAttribute("aria-busy") === "true";
    if (!checkoutBusy && !mutationBusy) return false;
    announce(root, checkoutBusy ? copy(root, "checkoutBusy") : copy(root, "cartBusy"));
    return true;
  };

  const stopMotion = (root, frames = 2) => {
    if (!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
    const run = {};
    reducedMotionRuns.set(root, run);
    const finishFrame = (remaining) => window.requestAnimationFrame(() => {
      if (reducedMotionRuns.get(root) !== run) return;
      const targets = [root, root?.shadowRoot].filter(Boolean);
      for (const target of targets) {
        for (const animation of target.getAnimations?.({ subtree: true }) || []) {
          try { animation.finish(); } catch { animation.cancel(); }
        }
      }
      if (remaining > 1 && root.isConnected) finishFrame(remaining - 1);
      else reducedMotionRuns.delete(root);
    });
    finishFrame(Math.max(1, frames));
  };

  const ensureFocusInside = (root) => {
    const documentFocus = document.activeElement;
    const shadowFocus = root.shadowRoot?.activeElement;
    if (documentFocus === root || root.contains(documentFocus) || shadowFocus) return;
    root.querySelector("[data-guru-cart-initial-focus], [data-cart-continue], .cart-empty__action")
      ?.focus({ preventScroll: true });
  };

  const syncCheckoutState = (root) => {
    if (!root) return;
    const memberBusy = memberCheckoutBusy(root);
    const mutationBusy = cartMutationBusy(root);
    const leaseBusy = commerceLeaseBusy();
    const localBusy = localMutationBusy(root);
    const sessionResolving = root.dataset.minGuruSessionState === "resolving";
    const busy = root.getAttribute("aria-busy") === "true"
      || memberBusy
      || mutationBusy
      || localBusy
      || leaseBusy;
    const failed = root.dataset.guruCartError === "true";
    const pending = pendingPlanControls(root).length > 0;
    const attention = root.querySelector("[data-guru-cart-line-blocking]") !== null;
    const blocked = busy || sessionResolving || failed || pending || attention;
    const checkout = root.querySelector("[data-cart-continue]");
    const label = root.querySelector("[data-guru-cart-checkout-label]");
    const lock = root.querySelector("[data-guru-cart-checkout-lock]");

    root.dataset.guruCartBlocked = String(blocked);
    const externallyBusy = busy && !localBusy;
    if (externallyBusy || failed) setMutationControlsLocked(root, true);
    else if (!localBusy) setMutationControlsLocked(root, false);
    setShadowFooterVisibility(root);
    if (!checkout) return;
    checkout.disabled = blocked;
    checkout.setAttribute("aria-busy", String(busy));
    if (lock) {
      lock.toggleAttribute("hidden", blocked);
      lock.setAttribute("aria-hidden", "true");
    }

    if (label) {
      const readyLabel = label.dataset.guruCartCheckoutReadyLabel
        || copy(root, "checkoutReady");
      label.textContent = sessionResolving
          ? copy(root, "checkoutLoading")
        : failed || pending || attention
          ? copy(root, "resolveChanges")
          : readyLabel;
    }
  };

  const setBusy = (root, busy) => {
    root.toggleAttribute("aria-busy", busy);
    if (busy) root.setAttribute("aria-busy", "true");
    const loading = root.querySelector("[data-guru-cart-loading]");
    const error = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    const failed = root.dataset.guruCartError === "true";
    if (loading) loading.hidden = !busy;
    if (error) error.hidden = busy || !failed;
    if (primary) primary.hidden = busy || failed;
    if (checkoutForm) checkoutForm.hidden = busy || failed;
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
  };

  const clearError = (root) => {
    root.dataset.guruCartError = "false";
    const error = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    if (error) error.hidden = true;
    if (primary && root.getAttribute("aria-busy") !== "true") primary.hidden = false;
    if (checkoutForm && root.getAttribute("aria-busy") !== "true") checkoutForm.hidden = false;
    retryMutations.delete(requestKeyFor(root));
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
  };

  const showError = (root, error, retry, { commerceLeaseRecovery = false } = {}) => {
    root.dataset.guruCartError = "true";
    const errorBox = root.querySelector("[data-guru-cart-error]");
    const primary = root.querySelector("[data-guru-cart-primary]");
    const checkoutForm = root.querySelector("[data-guru-cart-checkout-form]");
    const message = root.querySelector("[data-guru-cart-error-message]");
    const errorCopy = typeof error?.description === "string"
      ? error.description
      : typeof error?.message === "string"
        ? error.message
        : copy(root, "unchanged");
    if (message) message.textContent = errorCopy;
    if (errorBox) {
      errorBox.hidden = false;
      errorBox.scrollIntoView({ block: "nearest" });
    }
    if (primary) primary.hidden = true;
    if (checkoutForm) checkoutForm.hidden = true;
    retryMutations.set(requestKeyFor(root), { commerceLeaseRecovery, retry });
    syncCheckoutState(root);
    announce(root, copy(root, "updateFailed", { ERROR: errorCopy }));
    window.requestAnimationFrame(() => {
      const retryButton = errorBox?.querySelector("[data-guru-cart-retry]");
      (retryButton || errorBox)?.focus({ preventScroll: true });
    });
    if (window.themeVariables?.settings?.cartType === "drawer") {
      void root.show?.();
    }
  };

  const bundledSections = () => {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections },
      }),
    );
    return [...new Set(sections.filter(Boolean))];
  };

  const validCartPayload = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return false;
    const itemCount = Number(cart.item_count);
    const totalPrice = Number(cart.total_price);
    if (
      !Number.isSafeInteger(itemCount)
      || itemCount < 0
      || !Number.isSafeInteger(totalPrice)
      || totalPrice < 0
    ) return false;

    const keys = new Set();
    let quantityTotal = 0;
    for (const item of cart.items) {
      const key = String(item?.key || "");
      const quantity = Number(item?.quantity);
      if (
        !key
        || keys.has(key)
        || !Number.isSafeInteger(quantity)
        || quantity < 1
      ) return false;
      keys.add(key);
      quantityTotal += quantity;
    }
    return quantityTotal === itemCount;
  };

  const sectionLinesMatchCart = (primary, cart) => {
    const renderedLines = [...primary.querySelectorAll(
      "[data-guru-cart-lines] [data-cart-line-key], .cart-drawer__items [data-cart-line-key]",
    )];
    if (renderedLines.length !== cart.items.length) return false;

    const expected = new Map(
      cart.items.map((item) => [String(item.key), Number(item.quantity)]),
    );
    const rendered = new Map();
    for (const line of renderedLines) {
      const key = String(line.dataset.cartLineKey || "");
      const lineTotals = [...line.querySelectorAll("[data-min-guru-line-total]")];
      const giftTier = String(line.dataset.guruCartGiftLine || "");
      const quantity = giftTier
        ? 1
        : Number(line.querySelector("[data-guru-cart-quantity]")?.value);
      if (
        !key
        || rendered.has(key)
        || line.dataset.commerceIdentity !== key
        || lineTotals.length !== 1
        || lineTotals[0].dataset.commerceIdentity !== key
        || !Number.isSafeInteger(quantity)
        || quantity < 1
        || (giftTier && !["750", "1500"].includes(giftTier))
      ) return false;
      rendered.set(key, quantity);
    }
    return [...expected].every(
      ([key, quantity]) => rendered.get(key) === quantity,
    );
  };

  const parsedSectionRoot = (root, documentFragment) => {
    const sectionId = sectionIdFor(root);
    const section = documentFragment.getElementById(`shopify-section-${sectionId}`);
    if (!sectionId || !section) return { section: null, surface: null };
    if (isDrawerRoot(root)) {
      return {
        section,
        surface: section.querySelector("cart-drawer[data-guru-cart-drawer]"),
      };
    }
    if (isPageRoot(root)) {
      return {
        section,
        surface: section.querySelector("[data-guru-cart-page]"),
      };
    }
    return { section, surface: null };
  };

  const validSectionHtml = (root, html, cart) => {
    if (typeof html !== "string" || html.trim() === "") return "";
    const sectionId = sectionIdFor(root);
    if (!sectionId) return "";
    const documentFragment = new DOMParser().parseFromString(html, "text/html");
    const { section, surface } = parsedSectionRoot(root, documentFragment);
    const drawer = isDrawerRoot(root) ? surface : null;
    const page = isPageRoot(root) ? surface : null;
    const content = surface?.querySelector("[data-cart-content]");
    const primary = content?.querySelector("[data-guru-cart-primary]");
    const loading = content?.querySelector("[data-guru-cart-loading]");
    const error = content?.querySelector(".cart-error[data-guru-cart-error]");
    const errorMessage = error?.querySelector("[data-guru-cart-error-message]");
    const retry = error?.querySelector("[data-guru-cart-retry]");
    const liveRegion = surface?.querySelector('[data-guru-cart-status][role="status"][aria-live="polite"]');
    const itemCount = Number(cart?.item_count);
    const footer = drawer?.querySelector('[slot="footer"]')
      || page?.querySelector("[data-guru-cart-checkout-form]");
    const checkoutForm = footer?.matches?.("[data-guru-cart-checkout-form]")
      ? footer
      : footer?.querySelector("[data-guru-cart-checkout-form]");
    const checkout = checkoutForm?.querySelector("[data-cart-continue]");
    const viewCart = checkoutForm?.querySelector("[data-cart-view]");
    const renderedItemCount = Number(surface?.dataset.cartItemCount);
    const renderedTotal = Number(surface?.dataset.cartNativeTotal);
    const renderedCurrency = String(surface?.dataset.cartCurrency || "").toUpperCase();
    const authoritativeCurrency = String(cart?.currency || "").toUpperCase();
    const showCheckout = surface?.dataset.cartShowCheckout;
    const showDiscount = surface?.dataset.cartShowDiscount;
    const showNote = surface?.dataset.cartShowNote;
    const showViewCart = surface?.dataset.cartShowViewCart;
    const validConfiguration = [
      showCheckout,
      showDiscount,
      showNote,
      showViewCart,
    ]
      .every((value) => value === "true" || value === "false");
    if (
      !(isDrawerRoot(root)
        ? section?.matches("section.shopify-section.shopify-section--cart-drawer")
          && drawer?.matches("cart-drawer[data-guru-cart-drawer]")
          && drawer.getAttribute("template") === root.getAttribute("template")
          && drawer.querySelector('[slot="header"] [data-guru-cart-initial-focus]')
        : section?.matches("section.shopify-section.shopify-section--main-cart")
          && page?.matches("section.cart-panel[data-guru-cart-page]")
          && page.querySelector('.cart-panel__header [data-guru-cart-initial-focus]'))
      || !surface
      || surface.id !== root.id
      || surface.dataset.guruCartSectionId !== sectionId
      || surface.dataset.cartStorefrontOrigin !== root.dataset.cartStorefrontOrigin
      || surface.dataset.cartSurface !== root.dataset.cartSurface
      || !validConfiguration
      || !content
      || !primary
      || !loading
      || !loading.matches('[role="status"]')
      || !error
      || !error.matches('[role="alert"][tabindex="-1"]')
      || !errorMessage
      || !retry
      || !liveRegion
      || !Number.isFinite(itemCount)
      || renderedItemCount !== itemCount
      || renderedTotal !== Number(cart?.total_price)
      || !authoritativeCurrency
      || renderedCurrency !== authoritativeCurrency
      || (itemCount > 0 && !primary.querySelector(".cart-drawer__items"))
      || (itemCount > 0 && !sectionLinesMatchCart(primary, cart))
      || (itemCount === 0 && !primary.querySelector(".cart-empty .cart-empty__action"))
      || (itemCount === 0 && primary.querySelector("[data-cart-line-key]"))
      || (itemCount > 0 && (
        !footer
        || !checkoutForm
        || (showDiscount === "true") !== Boolean(checkoutForm.querySelector(".cart-summary__discount"))
        || (showNote === "true") !== Boolean(checkoutForm.querySelector('cart-note textarea[name="note"]'))
        || (showCheckout === "true") !== Boolean(checkout?.matches('button[type="submit"][name="checkout"]'))
        || (showViewCart === "true") !== Boolean(viewCart?.matches('a[href][data-cart-view]'))
        || (showCheckout === "true" && checkout.querySelector("[data-guru-cart-checkout-ready-label]")?.dataset.guruCartCheckoutReadyLabel !== copy(root, "checkoutReady"))
      ))
    ) return "";
    return html;
  };

  // Bound headers and body together. A lost POST response never authorizes replay.
  const cartRequest = async (url, options = {}) => {
    const controller = new AbortController();
    let timer;
    const deadline = new Promise((_, reject) => {
      timer = window.setTimeout(() => {
        controller.abort();
        reject(new Error(copy(document.querySelector(rootSelector), "tryAgain")));
      }, 15000);
    });
    try {
      return await Promise.race([deadline, (async () => {
        const response = await fetch(url, { ...options, signal: controller.signal });
        const body = await response.text();
        return new Response(body, { status: response.status, headers: response.headers });
      })()]);
    } finally {
      window.clearTimeout(timer);
    }
  };

  const readCart = async (root) => {
    const response = await cartRequest(route("cart.js"), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!response.ok) throw await responseError(root, response);
    const cart = await response.json();
    if (!validCartPayload(cart)) throw new Error(copy(root, "invalidCart"));
    return cart;
  };

  const readSection = async (root, cart) => {
    const sectionId = sectionIdFor(root);
    const sectionPath = isPageRoot(root) ? window.location.pathname : route("");
    const separator = sectionPath.includes("?") ? "&" : "?";
    const response = await cartRequest(`${sectionPath}${separator}section_id=${encodeURIComponent(sectionId)}`, {
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    });
    if (!response.ok) throw new Error(copy(root, "viewFetchFailed"));
    const html = validSectionHtml(root, await response.text(), cart);
    if (!html) throw new Error(copy(root, "invalidView"));
    return html;
  };

  const cartWithSection = async (root, cart, sections) => {
    if (!validCartPayload(cart)) throw new Error(copy(root, "invalidCart"));
    const roots = [...document.querySelectorAll(rootSelector)];
    if (!roots.includes(root)) roots.push(root);
    for (const currentRoot of roots) {
      const sectionId = sectionIdFor(currentRoot);
      const sectionHtml = validSectionHtml(
        currentRoot,
        sections?.[sectionId],
        cart,
      );
      if (!sectionHtml) {
        throw new Error(copy(root, "incompleteView"));
      }
    }
    return {
      ...cart,
      sections: { ...(cart.sections || {}), ...(sections || {}) },
    };
  };

  const readCurrentSections = async (cart) => {
    const entries = await Promise.all(
      [...document.querySelectorAll(rootSelector)].map(async (root) => [
        sectionIdFor(root),
        await readSection(root, cart),
      ]),
    );
    return Object.fromEntries(entries.filter(([sectionId]) => sectionId));
  };

  const waitForCartRefresh = (root, timeout = 2500) => new Promise((resolve, reject) => {
    let timer;
    const eventName = isPageRoot(root)
      ? "guru-cart-page:refreshed"
      : "cart-drawer:refreshed";
    const eventTarget = isPageRoot(root) ? document : root;
    const sectionId = sectionIdFor(root);
    const cleanup = () => {
      window.clearTimeout(timer);
      eventTarget.removeEventListener(eventName, onRefresh);
    };
    const onRefresh = (event) => {
      if (isPageRoot(root) && event.detail?.sectionId !== sectionId) return;
      cleanup();
      resolve(event.target);
    };
    eventTarget.addEventListener(eventName, onRefresh);
    timer = window.setTimeout(() => {
      cleanup();
      reject(new Error(copy(root, "viewNotUpdated")));
    }, timeout);
  });

  const commitCartChange = async (root, cart, baseEvent, onSuccessDo = "") => {
    const refresh = waitForCartRefresh(root);
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: { baseEvent, onSuccessDo, cart },
      }),
    );
    await refresh;
    return cart;
  };

  const guardCartChange = (event) => {
    const roots = [...document.querySelectorAll(rootSelector)];
    if (!roots.length) return;
    const cart = event.detail?.cart;
    const invalidRoot = roots.find((root) => !(
      validCartPayload(cart)
      && validSectionHtml(
        root,
        cart?.sections?.[sectionIdFor(root)],
        cart,
      )
    ));
    if (!invalidRoot) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    showError(
      invalidRoot,
      new Error(copy(invalidRoot, "incompleteView")),
      () => refreshOnly(invalidRoot, copy(invalidRoot, "cartUpdated"), "guru-cart:recovery"),
    );
    document.dispatchEvent(new CustomEvent("guru-cart:change-rejected"));
  };

  window.addEventListener("cart:change", guardCartChange, true);

  document.addEventListener("cart:prepare-bundled-sections", (event) => {
    if (!Array.isArray(event.detail?.sections)) return;
    document.querySelectorAll(pageRootSelector).forEach((root) => {
      const sectionId = sectionIdFor(root);
      if (sectionId) event.detail.sections.push(sectionId);
    });
  });

  document.addEventListener("cart:change", (event) => {
    if (event.defaultPrevented) return;
    const cart = event.detail?.cart;
    document.querySelectorAll(pageRootSelector).forEach((root) => {
      const sectionId = sectionIdFor(root);
      const html = cart?.sections?.[sectionId];
      if (!validSectionHtml(root, html, cart)) return;
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const replacement = parsedSectionRoot(root, parsed).surface;
      if (!replacement) return;
      root.replaceWith(replacement);
      initializeRoot(replacement);
      replacement.dispatchEvent(new CustomEvent("guru-cart-page:refreshed", {
        bubbles: true,
        detail: { sectionId },
      }));
    });
  }, true);

  window.addEventListener("submit", (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('form[action*="/cart/add"]')) return;
    if (window.GuruNativeProductForm?.ownsRecoveryLease?.(form)) return;
    const root = document.querySelector(rootSelector);
    const cardBlocked = Boolean(
      form.closest("[data-guru-product-card-form]")
      && root
      && (
        root.getAttribute("aria-busy") === "true"
        || root.dataset.guruCartLocalMutationBusy === "true"
        || root.dataset.minGuruCartMutationBusy === "true"
        || root.dataset.guruCartError === "true"
      )
    );
    if (!commerceLeaseBusy() && !cardBlocked) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    form.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: { error: copy(root, "cartBusy") },
    }));
  }, true);

  const responseError = async (root, response) => {
    const data = await response.json().catch(() => ({}));
    const description = data.description || data.message || copy(root, "tryAgain");
    return { ...data, description, httpStatus: response.status };
  };

  const parseProperties = (value) => {
    try { return JSON.parse(value || "{}"); } catch { return {}; }
  };

  const normalizedPropertyEntries = (value) => {
    if (value === null || value === undefined) return [];
    const source = Array.isArray(value) ? value : Object.entries(value);
    const normalized = [];
    const keys = new Set();
    for (const entry of source) {
      if (!Array.isArray(entry) || entry.length < 2) return null;
      const key = String(entry[0] || "");
      const propertyValue = entry[1];
      if (!key || keys.has(key)) return null;
      keys.add(key);
      if (
        propertyValue === null
        || propertyValue === undefined
        || String(propertyValue).trim() === ""
      ) continue;
      normalized.push([key, String(propertyValue)]);
    }
    return normalized.sort(([left], [right]) => left.localeCompare(right));
  };

  const stableProperties = (value) => {
    const entries = normalizedPropertyEntries(value);
    return entries === null ? null : JSON.stringify(entries);
  };

  const cartCommerceIdentity = (item) => {
    const variantId = Number(item?.variant_id || item?.id);
    const sellingPlanId = String(
      item?.selling_plan_allocation?.selling_plan?.id || "",
    );
    const properties = stableProperties(item?.properties);
    if (
      !Number.isSafeInteger(variantId)
      || variantId < 1
      || (sellingPlanId && !/^\d+$/.test(sellingPlanId))
      || properties === null
    ) return "";
    return JSON.stringify([String(variantId), sellingPlanId, properties]);
  };

  const cartCommerceSnapshot = (cart) => {
    if (!validCartPayload(cart)) return null;
    const snapshot = new Map();
    for (const item of cart.items) {
      const identity = cartCommerceIdentity(item);
      if (!identity) return null;
      snapshot.set(
        identity,
        (snapshot.get(identity) || 0) + Number(item.quantity),
      );
    }
    return snapshot;
  };

  const expectedAddSnapshot = (baseline, variantId) => {
    if (!baseline || !Number.isSafeInteger(variantId) || variantId < 1) return null;
    const identity = JSON.stringify([String(variantId), "", "[]"]);
    const expected = new Map(baseline);
    expected.set(identity, (expected.get(identity) || 0) + 1);
    return expected;
  };

  const snapshotsMatch = (left, right) => Boolean(
    left
    && right
    && left.size === right.size
    && [...left].every(([identity, quantity]) => right.get(identity) === quantity),
  );

  const requestFocusAfterRefresh = (root, line, control, focusOptions = {}) => {
    focusRequests.set(requestKeyFor(root), {
      lineKey: line?.dataset.cartLineKey || "",
      variantId: line?.dataset.variantId || "",
      sellingPlanId: line?.dataset.sellingPlanId || "",
      properties: stableProperties(parseProperties(line?.dataset.lineProperties)),
      fallbackLineKey: focusOptions.fallbackLineKey || "",
      nextLineKey: focusOptions.nextLineKey || "",
      previousLineKey: focusOptions.previousLineKey || "",
      control,
    });
  };

  const returnedLineKey = (cart, request) => {
    const matches = (cart?.items || []).filter((item) => {
      const planId = String(item.selling_plan_allocation?.selling_plan?.id || "");
      return String(item.variant_id || "") === request.variantId
        && planId === request.sellingPlanId
        && stableProperties(item.properties) === request.properties;
    });
    return matches.length === 1 ? String(matches[0].key || "") : "";
  };

  const restoreFocus = (root) => {
    const requestKey = requestKeyFor(root);
    const request = focusRequests.get(requestKey);
    if (!request) return;
    focusRequests.delete(requestKey);
    const escaped = (value) => window.CSS?.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, "\\$&");
    if (request.control === "gift") {
      root.querySelector(`[data-guru-cart-gift-tier="${escaped(request.tier)}"] > summary`)
        ?.focus({ preventScroll: true });
      return;
    }
    if (request.control === "recommendation") {
      const recommendation = request.recommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.recommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      (recommendation || root.querySelector("[data-cart-continue]"))?.focus({ preventScroll: true });
      return;
    }
    if (request.control === "recommendation-pointer") {
      const recommendation = request.recommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.recommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      const fallbackRecommendation = request.fallbackRecommendationProductId
        ? root.querySelector(`[data-cart-recommendation][data-product-id="${escaped(request.fallbackRecommendationProductId)}"] [data-guru-cart-quick-add]`)
        : null;
      (recommendation || fallbackRecommendation || root.querySelector("[data-cart-continue]"))
        ?.focus({ preventScroll: true });
      return;
    }
    if (request.control === "remove") {
      const nextLine = request.nextLineKey
        ? root.querySelector(`[data-cart-line-key="${escaped(request.nextLineKey)}"]`)
        : null;
      const previousLine = request.previousLineKey
        ? root.querySelector(`[data-cart-line-key="${escaped(request.previousLineKey)}"]`)
        : null;
      (nextLine?.querySelector("h2 a")
        || previousLine?.querySelector("h2 a")
        || root.querySelector(".cart-empty__action")
        || root.querySelector("[data-guru-cart-initial-focus]"))
        ?.focus({ preventScroll: true });
      return;
    }
    const line = root.querySelector(`[data-cart-line-key="${escaped(request.returnedLineKey || request.lineKey)}"]`)
      || (request.fallbackLineKey ? root.querySelector(`[data-cart-line-key="${escaped(request.fallbackLineKey)}"]`) : null);
    let target = null;

    if (line) {
      if (request.control === "frequency") target = line.querySelector("[data-guru-cart-frequency]");
      if (request.control === "toggle") target = line.querySelector("[data-guru-cart-plan-toggle]");
      if (request.control === "minus") target = line.querySelector('[data-guru-cart-quantity-action="minus"], [data-guru-cart-quantity-action="remove"]');
      if (request.control === "plus") {
        target = line.querySelector('[data-guru-cart-quantity-action="plus"]')
          || line.querySelector("[data-guru-cart-quantity]");
      }
      if (request.control === "quantity") target = line.querySelector("[data-guru-cart-quantity]");
      target ||= line.querySelector("h2 a, [data-guru-cart-quantity]");
    }

    target ||= root.querySelector("[data-cart-continue], .cart-empty__action");
    target?.focus({ preventScroll: true });
  };

  const applyQuantityLimit = (root) => {
    const requestKey = requestKeyFor(root);
    const request = quantityLimitRequests.get(requestKey);
    if (!request) return;
    quantityLimitRequests.delete(requestKey);
    const escaped = (value) => window.CSS?.escape ? CSS.escape(value) : String(value).replace(/["\\]/g, "\\$&");
    const line = root.querySelector(`[data-cart-line-key="${escaped(request.lineKey)}"]`);
    if (!line) return;

    const notice = line.querySelector("[data-guru-cart-line-notice]");
    if (notice) {
      notice.textContent = request.message;
      notice.hidden = false;
    }

    const input = line.querySelector("[data-guru-cart-quantity]");
    if (input) input.max = String(request.quantity);
    const plus = line.querySelector('[data-guru-cart-quantity-action="plus"]');
    if (plus) {
      const disabledPlus = document.createElement("button");
      disabledPlus.type = "button";
      disabledPlus.disabled = true;
      disabledPlus.setAttribute("aria-label", request.message);
      disabledPlus.dataset.guruCartInventoryLimit = "true";
      disabledPlus.textContent = "+";
      plus.replaceWith(disabledPlus);
    }
  };

  const refreshOnly = async (root, successCopy, baseEvent = "guru-cart:refresh") => {
    if (root.getAttribute("aria-busy") === "true" || memberCheckoutBusy(root)) return false;
    clearError(root);
    setBusy(root, true);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    try {
      const initialCart = await readCart(root);
      const reconciliation = await reconcileGiftLines(root, initialCart);
      const { cart } = reconciliation;
      const sections = cart === initialCart
        ? await readCurrentSections(cart)
        : cart.sections;
      const refreshedCart = await cartWithSection(root, cart, sections);
      announcementRequests.set(
        requestKeyFor(root),
        giftThresholdRemovalAnnouncement(root, reconciliation.thresholdRemovedTiers) || successCopy,
      );
      await commitCartChange(root, refreshedCart, baseEvent);
      return true;
    } catch (error) {
      showError(root, error, () => refreshOnly(root, successCopy, baseEvent));
      return false;
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setBusy(root, false);
    }
  };

  const giftTierForItem = (item) => String(item?.properties?._guru_gift_tier || "");

  const giftThresholdFor = (root, tier) => Number(
    tier === "750"
      ? root?.dataset.cartGiftOneThreshold
      : root?.dataset.cartGiftTwoThreshold,
  );

  const paidMerchandiseSubtotal = (cart) => (cart?.items || []).reduce(
    (total, item) => total + (giftTierForItem(item) ? 0 : Number(item.original_line_price ?? item.final_line_price ?? 0)),
    0,
  );

  const updateCartLines = async (root, updates, sections = []) => {
    const payload = { updates };
    if (sections.length) {
      payload.sections = sections.join(",");
      payload.sections_url = window.location.pathname;
    }
    const response = await cartRequest(route("cart/update.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw await responseError(root, response);
    const cart = await response.json();
    if (!validCartPayload(cart)) throw new Error(copy(root, "invalidCart"));
    return cart;
  };

  const giftLineRemovals = (root, cart) => {
    const paidSubtotal = paidMerchandiseSubtotal(cart);
    const seen = new Set();
    const removals = [];
    const thresholdRemovedTiers = new Set();

    for (const item of cart.items || []) {
      const tier = giftTierForItem(item);
      if (!tier) continue;
      const threshold = giftThresholdFor(root, tier);
      const key = String(item.key || "");
      let reason = "";
      if (!["750", "1500"].includes(tier)) reason = "invalid";
      else if (!Number.isSafeInteger(threshold) || threshold < 1) reason = "invalid";
      else if (paidSubtotal < threshold && !(Number(item.final_line_price) === 0
        && receiptPattern.test(item.properties?._guru_gift_receipt || ""))) reason = "threshold";
      else if (Number(item.quantity) !== 1) reason = "quantity";
      else if (seen.has(tier)) reason = "duplicate";
      else {
        seen.add(tier);
        if (Number(item.final_line_price) !== 0) reason = "paid";
      }
      if (!reason) continue;
      if (reason === "threshold") thresholdRemovedTiers.add(tier);
      removals.push({ key, tier, reason });
    }
    return { removals, thresholdRemovedTiers: [...thresholdRemovedTiers] };
  };

  const reconcileGiftLines = async (root, cart) => {
    const reconciliation = giftLineRemovals(root, cart);
    if (!reconciliation.removals.length) return { cart: await preserveNativeGiftReceipts(root, cart), thresholdRemovedTiers: [] };
    const updates = Object.fromEntries(
      reconciliation.removals.map(({ key }) => [key, 0]),
    );
    const current = await updateCartLines(root, updates, bundledSections());
    if (giftLineRemovals(root, current).removals.length) {
      throw new Error(copy(root, "giftNormalizeFailed"));
    }
    return {
      cart: await preserveNativeGiftReceipts(root, current),
      thresholdRemovedTiers: reconciliation.thresholdRemovedTiers,
    };
  };

  const giftReceiptSnapshot = (cart) => JSON.stringify([cart.currency, (cart.items || []).map(item => [
    item.variant_id, item.quantity, item.selling_plan_allocation?.selling_plan?.id || 0,
    Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b)),
  ]).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)))]);

  const preserveNativeGiftReceipts = async (root, cart) => {
    if (!(cart.items || []).some(giftTierForItem)) return cart;
    const snapshot = giftReceiptSnapshot(cart);
    if (snapshot === sealedGiftSnapshot) return cart;
    const response = await cartRequest("https://min.gurushop.dk/min-guru/api/cart/gift-receipts", {
      method: "POST", credentials: "omit", headers: {"Content-Type": "application/json"},
      body: JSON.stringify({currency: cart.currency, lines: cart.items.map(item => ({
        variantId: `gid://shopify/ProductVariant/${item.variant_id}`, quantity: item.quantity,
        ...(item.selling_plan_allocation?.selling_plan?.id
          ? {sellingPlanId: `gid://shopify/SellingPlan/${item.selling_plan_allocation.selling_plan.id}`} : {}),
        attributes: Object.entries(item.properties || {}).map(([key, value]) => ({key, value: String(value)})),
      }))}),
    });
    if (!response.ok) throw new Error(copy(root, "giftNotConfirmed"));
    const result = await response.json();
    if (result.contract !== "gift-market-receipt@1" || typeof result.enabled !== "boolean"
      || !Array.isArray(result.updates) || result.updates.length > 2 || (!result.enabled && result.updates.length)) {
      throw new Error(copy(root, "giftNotConfirmed"));
    }
    let current = cart;
    const tiers = new Set();
    for (const update of result.updates) {
      const selected = current.items.filter(item => `gid://shopify/ProductVariant/${item.variant_id}` === update.variantId
        && giftTierForItem(item) === update.tier);
      if (selected.length !== 1 || selected[0].quantity !== 1 || selected[0].final_line_price !== 0
        || !receiptPattern.test(update.receipt || "") || tiers.has(update.tier)) throw new Error(copy(root, "giftNotConfirmed"));
      tiers.add(update.tier);
      const changed = await cartRequest(route("cart/change.js"), {
        method: "POST", credentials: "same-origin", headers: {"Content-Type": "application/json", Accept: "application/json"},
        body: JSON.stringify({id: selected[0].key, quantity: 1,
          properties: {...selected[0].properties, _guru_gift_receipt: update.receipt},
          sections: bundledSections().join(","), sections_url: window.location.pathname}),
      });
      if (!changed.ok) throw await responseError(root, changed);
      current = await changed.json();
      if (!validCartPayload(current)) throw new Error(copy(root, "invalidCart"));
    }
    for (const update of result.updates) {
      const selected = current.items.filter(item => `gid://shopify/ProductVariant/${item.variant_id}` === update.variantId
        && giftTierForItem(item) === update.tier);
      if (selected.length !== 1 || selected[0].quantity !== 1 || selected[0].final_line_price !== 0
        || selected[0].properties?._guru_gift_receipt !== update.receipt) throw new Error(copy(root, "giftNotConfirmed"));
    }
    // A receipt-property write must not change paid quantities, plans or totals.
    const withoutReceipt = value => ({...value, items: value.items.map(item => ({...item,
      properties: Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => key !== "_guru_gift_receipt")),
    }))});
    if (giftReceiptSnapshot(withoutReceipt(cart)) !== giftReceiptSnapshot(withoutReceipt(current))
      || current.total_price !== cart.total_price) throw new Error(copy(root, "giftNotConfirmed"));
    sealedGiftSnapshot = giftReceiptSnapshot(current);
    return current;
  };

  const giftThresholdRemovalAnnouncement = (root, tiers) => {
    const removed = [...new Set(tiers || [])]
      .filter((tier) => ["750", "1500"].includes(tier))
      .sort((left, right) => Number(left) - Number(right));
    if (removed.length === 0) return "";
    if (removed.length === 1) {
      const threshold = removed[0] === "1500" ? "1 500" : "750";
      return copy(root, "giftRemovedOne", { THRESHOLD: threshold });
    }
    return copy(root, "giftRemovedBoth");
  };

  const normalizeSelectedGift = async (root, tier, variantId, cart) => {
    const tierLines = cart.items.filter((item) => giftTierForItem(item) === tier);
    const selectedLines = tierLines.filter((item) => Number(item.variant_id) === variantId);
    const selected = selectedLines[0];
    if (!selected) throw new Error(copy(root, "giftNotConfirmed"));
    const updates = Object.fromEntries(
      tierLines.flatMap((item) => {
        if (item === selected) {
          return Number(item.quantity) === 1
            ? []
            : [[String(item.key || ""), 1]];
        }
        return [[String(item.key || ""), 0]];
      }),
    );
    if (!Object.keys(updates).length) return cart;
    const current = await updateCartLines(root, updates, bundledSections());
    const confirmed = current.items.filter((item) => giftTierForItem(item) === tier);
    if (
      confirmed.length !== 1
      || Number(confirmed[0].variant_id) !== variantId
      || Number(confirmed[0].quantity) !== 1
    ) {
      throw new Error(copy(root, "giftNotConfirmed"));
    }
    return current;
  };

  const changeLine = async (
    root,
    mutation,
    successCopy,
    baseEvent = "line-item:change",
    operation = {},
  ) => {
    if (root.getAttribute("aria-busy") === "true" || blockMutationDuringCheckout(root)) return;
    clearError(root);
    setLocalOperation(root, true, operation);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let mutationCommitted = false;
    let postStarted = false;

    try {
      const sections = bundledSections();
      const payload = {
        id: mutation.id,
        quantity: mutation.quantity,
        sections: sections.join(","),
        sections_url: window.location.pathname,
      };
      if (Object.hasOwn(mutation, "selling_plan")) payload.selling_plan = mutation.selling_plan;
      const focusRequest = focusRequests.get(requestKeyFor(root));
      if (focusRequest && Object.hasOwn(mutation, "selling_plan")) {
        focusRequest.sellingPlanId = String(mutation.selling_plan || "");
      }

      postStarted = true;
      const response = await cartRequest(route("cart/change.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        postStarted = false;
        const error = await responseError(root, response);
        const previousQuantity = Number(operation.previousQuantity);
        const requestedQuantity = Number(mutation.quantity);
        const isQuantityIncrease = Number.isSafeInteger(previousQuantity)
          && Number.isSafeInteger(requestedQuantity)
          && requestedQuantity > previousQuantity;
        if (Number(error.httpStatus) !== 422 || !isQuantityIncrease) throw error;

        try {
          const initialCart = await readCart(root);
          const focusRequest = focusRequests.get(requestKeyFor(root));
          const authoritativeLine = initialCart.items.find((item) => String(item.key) === String(mutation.id))
            || initialCart.items.find((item) => (
              focusRequest
              && String(item.variant_id || "") === focusRequest.variantId
              && String(item.selling_plan_allocation?.selling_plan?.id || "") === focusRequest.sellingPlanId
              && stableProperties(item.properties) === focusRequest.properties
            ));
          const authoritativeQuantity = Number(authoritativeLine?.quantity);
          if (
            !Number.isSafeInteger(authoritativeQuantity)
            || authoritativeQuantity < previousQuantity
            || authoritativeQuantity >= requestedQuantity
          ) throw error;
          mutationCommitted = true;

          const reconciliation = await reconcileGiftLines(root, initialCart);
          const { cart: reconciledCart } = reconciliation;
          const sections = reconciledCart === initialCart
            ? await readCurrentSections(reconciledCart)
            : reconciledCart.sections;
          const cart = await cartWithSection(root, reconciledCart, sections);
          if (focusRequest) focusRequest.returnedLineKey = returnedLineKey(cart, focusRequest);
          const title = String(operation.lineTitle || "").trim();
          const message = authoritativeQuantity > previousQuantity
            ? copy(root, "inventoryCapped", { TITLE: title, COUNT: authoritativeQuantity })
            : copy(root, "inventoryLimit", { TITLE: title });
          quantityLimitRequests.set(requestKeyFor(root), {
            lineKey: String(focusRequest?.returnedLineKey || authoritativeLine.key),
            message,
            quantity: authoritativeQuantity,
          });
          announcementRequests.set(requestKeyFor(root), message);
          await commitCartChange(root, cart, baseEvent);
          return;
        } catch (recoveryError) {
          if (recoveryError !== error) throw recoveryError;
          throw error;
        }
      }
      mutationCommitted = true;
      const responseCart = await response.json();
      const reconciliation = await reconcileGiftLines(root, responseCart);
      const { cart: reconciledCart } = reconciliation;
      const renderedSections = reconciledCart.sections;
      const cart = await cartWithSection(root, reconciledCart, renderedSections);
      if (focusRequest) focusRequest.returnedLineKey = returnedLineKey(cart, focusRequest);
      announcementRequests.set(
        requestKeyFor(root),
        giftThresholdRemovalAnnouncement(root, reconciliation.thresholdRemovedTiers) || successCopy,
      );
      await commitCartChange(root, cart, baseEvent);
    } catch (error) {
      if (mutationCommitted || postStarted) {
        await refreshOnly(root, mutationCommitted ? successCopy : copy(root, "cartUpdated"), `${baseEvent}:refresh`);
        return;
      }
      showError(
        root,
        error,
        () => changeLine(root, mutation, successCopy, baseEvent, operation),
      );
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setLocalOperation(root, false, operation);
    }
  };

  const addVariant = async (root, button) => {
    if (root.getAttribute("aria-busy") === "true" || blockMutationDuringCheckout(root)) return;
    const variantId = Number(button.dataset.guruCartQuickAdd);
    if (!Number.isInteger(variantId) || variantId < 1) return;
    if (button.dataset.guruCartRequiresSellingPlan !== "false") {
      announce(root, copy(root, "sellingPlanRequired"));
      return;
    }
    const recommendation = button.closest("[data-cart-recommendation]");
    const recommendationItem = recommendation?.closest("li");
    const nextRecommendation = recommendationItem?.nextElementSibling
      ?.querySelector?.("[data-cart-recommendation]");
    const recommendationFocus = inputModality === "keyboard"
      ? {
          control: "recommendation",
          recommendationProductId: nextRecommendation?.dataset.productId || "",
        }
      : {
          control: "recommendation-pointer",
          recommendationProductId: recommendation?.dataset.productId || "",
          fallbackRecommendationProductId: nextRecommendation?.dataset.productId || "",
        };

    clearError(root);
    const operation = { button, copy: copy(root, "adding") };
    setLocalOperation(root, true, operation);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

    let postStarted = false;
    let additionVerified = false;

    try {
      const baselineCart = await readCart(root);
      const baseline = cartCommerceSnapshot(baselineCart);
      const expected = expectedAddSnapshot(baseline, variantId);
      if (!baseline || !expected) {
        throw new Error(copy(root, "statusNotConfirmed"));
      }
      const sections = bundledSections();
      const formData = new FormData();
      formData.set("id", String(variantId));
      formData.set("quantity", "1");
      formData.set("sections", sections.join(","));
      formData.set("sections_url", window.location.pathname);

      postStarted = true;
      const response = await cartRequest(route("cart/add.js"), {
        method: "POST",
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: formData,
      });

      if (!response.ok) throw await responseError(root, response);
      const added = await response.json();
      const authoritativeCart = await readCart(root);
      if (!snapshotsMatch(cartCommerceSnapshot(authoritativeCart), expected)) {
        throw new Error(copy(root, "itemNotConfirmed"));
      }
      additionVerified = true;
      focusRequests.set(requestKeyFor(root), recommendationFocus);
      const cart = await cartWithSection(root, authoritativeCart, added.sections);
      announcementRequests.set(
        requestKeyFor(root),
        copy(root, "productAdded"),
      );
      await commitCartChange(root, cart, "variant:add");
    } catch (error) {
      const retry = postStarted
        ? () => refreshOnly(
            root,
            additionVerified ? copy(root, "productAdded") : copy(root, "cartUpdated"),
            "variant:add:refresh",
          )
        : () => addVariant(root, button);
      showError(root, error, retry);
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setLocalOperation(root, false, operation);
    }
  };

  const addGiftVariant = async (root, button) => {
    if (root.getAttribute("aria-busy") === "true" || blockMutationDuringCheckout(root)) return;
    const tier = String(button.dataset.guruCartGiftSelect || "");
    const variantId = Number(button.dataset.guruCartGiftVariant);
    const giftTitle = String(button.dataset.guruCartGiftTitle || copy(root, "giftFallback"));
    if (!["750", "1500"].includes(tier) || !Number.isSafeInteger(variantId) || variantId < 1) return;

    const operation = { button, copy: copy(root, "giftAdding") };
    clearError(root);
    setLocalOperation(root, true, operation);
    document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));
    let postStarted = false;
    try {
      const baseline = await readCart(root);
      const existing = baseline.items.filter((item) => giftTierForItem(item) === tier);
      const threshold = giftThresholdFor(root, tier);
      if (!Number.isSafeInteger(threshold) || paidMerchandiseSubtotal(baseline) < threshold) {
        throw new Error(copy(root, "giftThresholdLost"));
      }

      let authoritativeCart = baseline;
      let finalSections = null;
      if (!existing.some((item) => Number(item.variant_id) === variantId)) {
        const sections = bundledSections();
        const formData = new FormData();
        formData.set("id", String(variantId));
        formData.set("quantity", "1");
        formData.set("properties[_guru_gift_tier]", tier);
        formData.set("properties[_guru_gift_version]", "1");
        formData.set("sections", sections.join(","));
        formData.set("sections_url", window.location.pathname);
        postStarted = true;
        const addResponse = await cartRequest(route("cart/add.js"), {
          method: "POST",
          credentials: "same-origin",
          headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: formData,
        });
        if (!addResponse.ok) throw await responseError(root, addResponse);
        finalSections = (await addResponse.json()).sections || null;
        authoritativeCart = await readCart(root);
      }

      postStarted ||= existing.length > 1 || existing.some((item) => Number(item.quantity) !== 1);
      authoritativeCart = await normalizeSelectedGift(root, tier, variantId, authoritativeCart);
      authoritativeCart = await preserveNativeGiftReceipts(root, authoritativeCart);
      finalSections = authoritativeCart.sections || finalSections;
      const selected = authoritativeCart.items.filter((item) => giftTierForItem(item) === tier);
      if (
        selected.length !== 1
        || Number(selected[0].variant_id) !== variantId
        || Number(selected[0].quantity) !== 1
        || Number(selected[0].final_line_price) !== 0
      ) throw new Error(copy(root, "giftNotConfirmed"));

      const roots = [...document.querySelectorAll(rootSelector)];
      if (!roots.every((current) => validSectionHtml(current, finalSections?.[sectionIdFor(current)], authoritativeCart))) {
        finalSections = await readCurrentSections(authoritativeCart);
      }
      const cart = await cartWithSection(root, authoritativeCart, finalSections);
      focusRequests.set(requestKeyFor(root), { control: "gift", tier });
      announcementRequests.set(requestKeyFor(root), copy(root, "giftSelected", { TITLE: giftTitle }));
      await commitCartChange(root, cart, "gift:add");
    } catch (error) {
      // Even an existing paid/invalid gift must recover from authoritative state.
      if (postStarted) {
        await refreshOnly(root, copy(root, "cartUpdated"), "gift:recovery");
      } else {
        showError(root, error, () => refreshOnly(root, copy(root, "cartUpdated"), "gift:recovery"));
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      if (document.contains(root)) setLocalOperation(root, false, operation);
    }
  };

  const togglePlan = (root, toggle) => {
    if (blockMutationDuringCheckout(root)) return;
    const line = toggle.closest("[data-cart-line-key]");
    const frequency = line?.querySelector("[data-guru-cart-frequency]");
    const frequencyWrap = frequency?.closest(".cart-frequency");
    if (!line || !frequency || !frequencyWrap) return;

    const committed = line.querySelector("[data-guru-cart-frequency] option:checked")?.value || "";
    const pending = toggle.dataset.pending === "true";
    const description = line.querySelector("[data-guru-cart-plan-description]");
    const shouldRestoreFocus = inputModality === "keyboard";

    if (pending) {
      delete toggle.dataset.pending;
      toggle.setAttribute("aria-checked", "false");
      toggle.setAttribute("aria-expanded", "false");
      frequency.value = "";
      frequency.disabled = true;
      frequencyWrap.hidden = true;
      line.querySelector("[data-cart-plan-state]")?.setAttribute("data-cart-plan-state", "single");
      if (description) description.textContent = copy(root, "currentOneTime");
      syncCheckoutState(root);
      announce(root, copy(root, "fixedNotActivated"));
      if (shouldRestoreFocus) toggle.focus({ preventScroll: true });
      return;
    }

    if (committed) {
      requestFocusAfterRefresh(root, line, "toggle");
      void changeLine(
        root,
        { id: line.dataset.cartLineKey, quantity: Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1), selling_plan: null },
        copy(root, "changedToOneTime"),
        "line-item:change",
        { line },
      );
      return;
    }

    toggle.dataset.pending = "true";
    toggle.setAttribute("aria-checked", "true");
    toggle.setAttribute("aria-expanded", "true");
    frequencyWrap.hidden = false;
    frequency.disabled = false;
    frequency.value = "";
    line.querySelector("[data-cart-plan-state]")?.setAttribute("data-cart-plan-state", "pending");
    if (description) {
      description.textContent = copy(root, "fixedChooseInterval");
    }
    syncCheckoutState(root);
    announce(root, copy(root, "chooseInterval"));
    if (shouldRestoreFocus) frequency.focus({ preventScroll: true });
  };

  const changeFrequency = (root, select) => {
    if (blockMutationDuringCheckout(root)) return;
    const line = select.closest("[data-cart-line-key]");
    const sellingPlan = Number(select.value);
    if (!line || !Number.isInteger(sellingPlan) || sellingPlan < 1) {
      announce(root, copy(root, "chooseValidInterval"));
      return;
    }

    requestFocusAfterRefresh(root, line, "frequency");
    void changeLine(
      root,
      {
        id: line.dataset.cartLineKey,
        quantity: Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1),
        selling_plan: sellingPlan,
      },
      copy(root, "fixedUpdated", {
        INTERVAL: select.options[select.selectedIndex].text.trim()
          .toLocaleLowerCase(document.documentElement.lang || undefined),
      }),
      "line-item:change",
      { line },
    );
  };

  const changeQuantity = (root, line, quantity, control, focusOptions = {}) => {
    if (blockMutationDuringCheckout(root)) return;
    const input = line.querySelector("[data-guru-cart-quantity]");
    const bounded = Number(quantity) === 0
      ? 0
      : quantityOnLattice(input, quantity, control === "minus" ? "down" : control === "plus" ? "up" : "nearest");
    requestFocusAfterRefresh(root, line, control, focusOptions);
    const removalCopy = line.hasAttribute("data-guru-cart-gift-line")
      ? copy(root, "giftRemoved")
      : copy(root, "productRemoved");
    void changeLine(
      root,
      { id: line.dataset.cartLineKey, quantity: bounded },
      bounded === 0 ? removalCopy : copy(root, "quantityUpdated", { COUNT: bounded }),
      "line-item:change",
      {
        line,
        lineTitle: line.querySelector("h2")?.textContent?.trim() || "",
        previousQuantity: Number(line.dataset.cartLineQuantity),
      },
    );
  };

  document.addEventListener("pointerdown", () => {
    inputModality = "pointer";
  }, true);

  document.addEventListener("keydown", () => {
    inputModality = "keyboard";
  }, true);

  document.addEventListener("click", (event) => {
    const retry = event.target.closest?.("[data-guru-cart-retry]");
    if (retry) {
      event.preventDefault();
      const root = cartRoot(retry);
      const requestKey = requestKeyFor(root);
      const pendingRetry = retryMutations.get(requestKey);
      if (commerceLeaseBusy() && !pendingRetry?.commerceLeaseRecovery) {
        event.stopImmediatePropagation();
        announce(root, copy(root, "cartBusy"));
        return;
      }
      retryMutations.delete(requestKey);
      pendingRetry?.retry?.();
      return;
    }

    const quickAdd = event.target.closest?.("[data-guru-cart-quick-add]");
    if (quickAdd) {
      const root = cartRoot(quickAdd);
      if (!root) return;
      event.preventDefault();
      if (blockMutationDuringCheckout(root)) {
        event.stopImmediatePropagation();
        return;
      }
      void addVariant(root, quickAdd);
      return;
    }

    const giftSelect = event.target.closest?.("[data-guru-cart-gift-select]");
    if (giftSelect) {
      const root = cartRoot(giftSelect);
      if (!root) return;
      event.preventDefault();
      if (blockMutationDuringCheckout(root)) {
        event.stopImmediatePropagation();
        return;
      }
      void addGiftVariant(root, giftSelect);
      return;
    }

    const planToggle = event.target.closest?.("[data-guru-cart-plan-toggle]");
    if (planToggle) {
      const root = cartRoot(planToggle);
      if (!root) return;
      event.preventDefault();
      if (blockMutationDuringCheckout(root)) {
        event.stopImmediatePropagation();
        return;
      }
      togglePlan(root, planToggle);
      return;
    }

    const quantityAction = event.target.closest?.("[data-guru-cart-quantity-action]");
    if (quantityAction) {
      const root = cartRoot(quantityAction);
      const line = quantityAction.closest("[data-cart-line-key]");
      if (!root || !line) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (blockMutationDuringCheckout(root)) return;
      const current = Number(line.querySelector("[data-guru-cart-quantity]")?.value || 1);
      const type = quantityAction.dataset.guruCartQuantityAction;
      const lines = [...root.querySelectorAll("[data-cart-line-key]")];
      const index = lines.indexOf(line);
      const nextLineKey = lines[index + 1]?.dataset.cartLineKey || "";
      const previousLineKey = lines[index - 1]?.dataset.cartLineKey || "";
      const input = line.querySelector("[data-guru-cart-quantity]");
      const { step } = quantityRules(input);
      if (type === "remove") {
        changeQuantity(root, line, 0, "remove", { nextLineKey, previousLineKey });
      }
      if (type === "minus") changeQuantity(root, line, current - step, "minus");
      if (type === "plus") changeQuantity(root, line, current + step, "plus");
    }
  }, true);

  document.addEventListener("change", (event) => {
    const frequency = event.target.closest?.("[data-guru-cart-frequency]");
    if (frequency) {
      const root = cartRoot(frequency);
      if (root) {
        if (blockMutationDuringCheckout(root)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          return;
        }
        changeFrequency(root, frequency);
      }
      return;
    }

    const quantity = event.target.closest?.("[data-guru-cart-quantity]");
    if (quantity) {
      const root = cartRoot(quantity);
      const line = quantity.closest("[data-cart-line-key]");
      if (!root || !line) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (blockMutationDuringCheckout(root)) return;
      const bounded = quantityOnLattice(quantity, quantity.value);
      quantity.value = String(bounded);
      changeQuantity(root, line, bounded, "quantity");
    }
  }, true);

  document.addEventListener("toggle", (event) => {
    const details = event.target.closest?.("[data-guru-cart-gift-tier]");
    if (!details?.open) return;
    const root = cartRoot(details);
    root?.querySelectorAll?.("[data-guru-cart-gift-tier][open]").forEach((candidate) => {
      if (candidate !== details) candidate.removeAttribute("open");
    });
  }, true);

  // Seal an existing qualified cart before the native market selector changes currency.
  const approvedGiftMarkets = new WeakSet();
  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.('form');
    if (!form?.querySelector('[name="form_type"][value="localization"]')
      || event.submitter?.name !== "country_code") return;
    if (approvedGiftMarkets.has(form)) { approvedGiftMarkets.delete(form); return; }
    const root = document.querySelector(rootSelector);
    if (!root?.querySelector('[data-guru-cart-gift-line]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    void window[namespace].preserveGifts().then(() => {
      if (form.isConnected && submitter.isConnected) {
        approvedGiftMarkets.add(form);
        form.requestSubmit(submitter);
      }
    }).catch(error => showError(document.querySelector(rootSelector) || root, error,
      () => refreshOnly(root, copy(root, "cartUpdated"))));
  }, true);

  document.addEventListener("submit", (event) => {
    const form = event.target.closest?.("[data-guru-cart-checkout-form]");
    const root = cartRoot(form);
    if (!form || !root || event.submitter?.name !== "checkout") return;
    if (approvedGiftCheckouts.has(form)) { approvedGiftCheckouts.delete(form); return; }
    syncCheckoutState(root);
    if (root.dataset.guruCartBlocked !== "true" && root.querySelector("[data-guru-cart-gift-line]")) {
      event.preventDefault();
      event.stopImmediatePropagation();
      void window[namespace].preserveGifts().then(() => {
        const refreshedRoot = document.querySelector(isDrawerRoot(root) ? drawerRootSelector : pageRootSelector);
        const nextForm = refreshedRoot?.querySelector("[data-guru-cart-checkout-form]");
        const submitter = nextForm?.querySelector('[name="checkout"]');
        if (nextForm && submitter && !submitter.disabled) {
          approvedGiftCheckouts.add(nextForm);
          nextForm.requestSubmit(submitter);
        }
      }).catch(error => showError(document.querySelector(rootSelector) || root, error, () => refreshOnly(root, copy(root, "cartUpdated"))));
      return;
    }
    if (root.dataset.guruCartBlocked !== "true") return;
    event.preventDefault();
    event.stopImmediatePropagation();
    announce(root, pendingPlanControls(root).length
      ? copy(root, "checkoutInterval")
      : root.querySelector("[data-guru-cart-line-blocking]")
        ? copy(root, "checkoutUnavailable")
        : copy(root, "checkoutRefresh"));
  }, true);

  const onCartRootRefreshed = (event) => {
    const root = event.target.matches?.(rootSelector) ? event.target : cartRoot(event.target);
    if (!root) return;
    root.dataset.guruCartError = "false";
    setBusy(root, false);
    syncCheckoutState(root);
    window.requestAnimationFrame(() => {
      applyQuantityLimit(root);
      restoreFocus(root);
      const requestKey = requestKeyFor(root);
      const announcement = announcementRequests.get(requestKey);
      if (announcement) {
        announcementRequests.delete(requestKey);
        announce(root, announcement);
      }
    });
  };

  document.addEventListener("cart-drawer:refreshed", onCartRootRefreshed);
  document.addEventListener("guru-cart-page:refreshed", onCartRootRefreshed);
  document.addEventListener("guru:commerce-lease:change", () => {
    document.querySelectorAll(rootSelector).forEach(syncCheckoutState);
  });

  const initializeRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    if (isDrawerRoot(root)) {
      root.addEventListener("dialog:before-show", () => stopMotion(root, 120));
      root.addEventListener("dialog:after-show", () => {
        stopMotion(root, 120);
        window.requestAnimationFrame(() => ensureFocusInside(root));
      });
      root.addEventListener("dialog:before-hide", () => stopMotion(root, 120));
    }
    syncCheckoutState(root);
    setShadowFooterVisibility(root);
    stopMotion(root);
    window.queueMicrotask(() => {
      if (
        !root.isConnected
        || !root.querySelector('[data-cart-benefit-attention][data-cart-invalid-gift="true"]')
        || giftRecoveryPromise
      ) return;
      giftRecoveryPromise = refreshOnly(root, copy(root, "giftRecovered"), "gift:recovery")
        .finally(() => { giftRecoveryPromise = null; });
    });
  };

  for (const cartEvent of ["cart:change", "cart:refresh"]) {
    document.addEventListener(cartEvent, () => {
      document.querySelectorAll(rootSelector).forEach((root) => stopMotion(root, 120));
    });
  }

  document.addEventListener("shopify:section:load", (event) => {
    if (event.target.matches?.(rootSelector)) initializeRoot(event.target);
    event.target.querySelectorAll?.(rootSelector).forEach(initializeRoot);
  });

  document.querySelectorAll(rootSelector).forEach(initializeRoot);

  window[namespace] = {
    version: 1,
    preserveGifts: async () => {
      const root = document.querySelector(rootSelector);
      if (!root) return;
      if (commerceLeaseBusy() || [...document.querySelectorAll(rootSelector)].some(current =>
        current.getAttribute("aria-busy") === "true" || memberCheckoutBusy(current))) throw new Error(copy(root, "checkoutRefresh"));
      document.querySelectorAll(rootSelector).forEach(current => setBusy(current, true));
      try {
        const before = await readCart(root);
        const after = await preserveNativeGiftReceipts(root, before);
        if (after !== before) await commitCartChange(root, await cartWithSection(root, after, after.sections), "gift:receipt");
      } finally {
        document.querySelectorAll(rootSelector).forEach(current => setBusy(current, false));
      }
    },
    refresh: () => document.querySelectorAll(rootSelector).forEach(syncCheckoutState),
    prepareCart: async (cart, sections) => {
      const root = document.querySelector(rootSelector);
      if (!root) throw new Error(fallbackCopy.viewMissing);
      return cartWithSection(root, cart, sections);
    },
    validateCart: (cart, sections) => {
      const root = document.querySelector(rootSelector);
      return Boolean(
        root
        && validCartPayload(cart)
        && validSectionHtml(root, sections?.[sectionIdFor(root)], cart),
      );
    },
    commitCart: async ({ cart, baseEvent = "variant:add", onSuccessDo = "" } = {}) => {
      const root = document.querySelector(rootSelector);
      if (!root) throw new Error(fallbackCopy.viewMissing);
      return commitCartChange(root, cart, baseEvent, onSuccessDo);
    },
    showError: (error, retry, options) => {
      const root = document.querySelector(rootSelector);
      if (root) showError(root, error, retry, options);
    },
    clearError: () => {
      const root = document.querySelector(rootSelector);
      if (root) clearError(root);
    },
    isMutationPending: () => {
      const root = document.querySelector(rootSelector);
      return commerceLeaseBusy() || Boolean(root && (
        root.getAttribute("aria-busy") === "true"
        || root.dataset.guruCartLocalMutationBusy === "true"
        || root.dataset.minGuruCartMutationBusy === "true"
        || root.dataset.guruCartError === "true"
      ));
    },
  };
})();
